import React, { useEffect, useState, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

// ── Style primitives ─────────────────────────────────────────────────────────
const C = {
  bg: 'var(--theme-border-light)', cardBg: '#ffffff', border: 'var(--theme-border)',
  primary: '#1e3a5f', accent: '#3b82f6', success: '#22c55e',
  warn: '#f59e0b', danger: '#ef4444', text: '#1e293b', textMuted: 'var(--theme-text-secondary)',
  headerBg: '#1e3a5f', headerText: '#ffffff'
};

const card = {
  background: C.cardBg, border: `1px solid ${C.border}`,
  borderRadius: '12px', padding: '1rem 1.2rem',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
};

const secHead = {
  margin: '0 0 0.75rem', fontSize: '0.88rem', fontWeight: 700,
  color: C.primary, paddingBottom: '0.4rem', borderBottom: `2px solid ${C.border}`,
  textTransform: 'uppercase', letterSpacing: '0.04em'
};

// ── Priority colours ─────────────────────────────────────────────────────────
const PRIORITY_COLOURS = {
  CRITICAL: '#dc3545',
  HIGH: '#fd7e14',
  MEDIUM: '#ffc107',
  LOW: '#28a745'
};

const PRIORITY_ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

// ── Status badge colours ─────────────────────────────────────────────────────
const STATUS_COLOURS = {
  OPEN: '#3b82f6',
  IN_PROGRESS: '#f59e0b',
  ON_HOLD: '#6c757d',
  COMPLETED: '#22c55e',
  CANCELLED: '#dc3545',
  CLOSED: '#6f42c1'
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

const fmtDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(2);
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yy} ${hh}:${mi}`;
};

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, colour = C.accent, icon }) {
  return (
    <div style={{ ...card, borderTop: `4px solid ${colour}`, minWidth: '140px', flex: '1 1 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: '0.75rem', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        {icon && <span style={{ fontSize: '1.1rem' }}>{icon}</span>}
      </div>
      <div style={{ fontSize: '1.45rem', fontWeight: 800, color: C.text, margin: '0.3rem 0 0' }}>{value}</div>
    </div>
  );
}

// ── Priority Bar ─────────────────────────────────────────────────────────────
function PriorityBar({ label, count, total, colour }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ marginBottom: '0.65rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: C.text }}>{label}</span>
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: colour }}>{fmtNum(count)}</span>
      </div>
      <div style={{ height: '8px', background: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: colour, borderRadius: '4px', transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

// ── Status Row ───────────────────────────────────────────────────────────────
function StatusRow({ status, count }) {
  const colour = STATUS_COLOURS[status] || '#6c757d';
  const label = (status || '').replace(/_/g, ' ');
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.45rem 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: colour }} />
        <span style={{ fontSize: '0.84rem', fontWeight: 500, color: C.text, textTransform: 'capitalize' }}>{label}</span>
      </div>
      <span style={{ fontSize: '0.9rem', fontWeight: 700, color: C.text, background: '#f1f5f9', padding: '0.15rem 0.6rem', borderRadius: '12px' }}>{fmtNum(count)}</span>
    </div>
  );
}

// ── Main MaintDashboard ──────────────────────────────────────────────────────
function MaintDashboard({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [riskData, setRiskData] = useState(null);
  const [riskLoading, setRiskLoading] = useState(false);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  const loadDashboard = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/maintenance/dashboard', { headers: headers() });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error('MaintDashboard load error:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, headers]);

  const loadRisk = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setRiskLoading(true);
    try {
      const res = await fetch('/api/v1/maintenance/predictive-risk', { headers: headers() });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const json = await res.json();
      setRiskData(json);
    } catch (err) {
      console.error('MaintDashboard predictive-risk load error:', err);
    } finally {
      setRiskLoading(false);
    }
  }, [selectedProperty, headers]);

  useEffect(() => { loadDashboard(); loadRisk(); }, [loadDashboard, loadRisk]);

  // ── Derived values ─────────────────────────────────────────────────────────
  const summary = data?.summary || {};
  const byPriority = data?.by_priority || [];
  const byStatus = data?.by_status || [];
  const recentWOs = data?.recent_work_orders || [];
  const totalByPriority = byPriority.reduce((s, r) => s + Number(r.count || 0), 0);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (!selectedProperty?.id) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: C.textMuted, fontSize: '0.95rem' }}>
        Please select a property to view the Maintenance Dashboard.
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem 1.5rem', background: C.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: C.primary }}>Maintenance Dashboard</h2>
          <div style={{ fontSize: '0.78rem', color: C.textMuted, marginTop: '0.15rem' }}>
            Real-time overview of work orders and preventive maintenance
          </div>
        </div>
        <button
          onClick={loadDashboard}
          disabled={loading}
          style={{
            padding: '0.45rem 1rem', fontSize: '0.82rem', fontWeight: 600,
            background: C.primary, color: '#fff', border: 'none', borderRadius: '8px',
            cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div style={{
          padding: '0.7rem 1rem', marginBottom: '1rem', background: '#fef2f2',
          border: '1px solid #fecaca', borderRadius: '8px', color: '#b91c1c',
          fontSize: '0.85rem', fontWeight: 500
        }}>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div style={{ textAlign: 'center', padding: '3rem', color: C.textMuted, fontSize: '0.95rem' }}>
          Loading dashboard data...
        </div>
      )}

      {/* Dashboard content */}
      {data && (
        <>
          {/* ── Summary KPI Cards ─────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.2rem' }}>
            <KpiCard label="Open WOs" value={fmtNum(summary.open_work_orders)} colour={C.accent} icon="📋" />
            <KpiCard label="Overdue" value={fmtNum(summary.overdue)} colour={C.danger} icon="⏰" />
            <KpiCard label="Completed Today" value={fmtNum(summary.completed_today)} colour={C.success} icon="✅" />
            <KpiCard label="Rooms OOO" value={fmtNum(summary.rooms_ooo)} colour="#6f42c1" icon="🚪" />
            <KpiCard label="PM Due Today" value={fmtNum(summary.pm_due_today)} colour={C.warn} icon="🔧" />
            <KpiCard label="SLA Breaches" value={fmtNum(summary.sla_breaches)} colour="#dc3545" icon="🚨" />
          </div>

          {/* ── Priority + Status side by side ────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: '0.75rem', marginBottom: '1.2rem' }}>
            {/* By Priority */}
            <div style={{ ...card }}>
              <h3 style={secHead}>By Priority</h3>
              {PRIORITY_ORDER.map((p) => {
                const row = byPriority.find((r) => r.priority === p);
                return (
                  <PriorityBar
                    key={p}
                    label={p}
                    count={Number(row?.count || 0)}
                    total={totalByPriority}
                    colour={PRIORITY_COLOURS[p]}
                  />
                );
              })}
              {byPriority.length === 0 && (
                <div style={{ fontSize: '0.84rem', color: C.textMuted, textAlign: 'center', padding: '1rem 0' }}>No data</div>
              )}
            </div>

            {/* By Status */}
            <div style={{ ...card }}>
              <h3 style={secHead}>By Status</h3>
              {byStatus.length > 0 ? byStatus.map((row) => (
                <StatusRow key={row.status} status={row.status} count={Number(row.count || 0)} />
              )) : (
                <div style={{ fontSize: '0.84rem', color: C.textMuted, textAlign: 'center', padding: '1rem 0' }}>No data</div>
              )}
            </div>
          </div>

          {/* ── Predictive Maintenance Risk ──────────────────────────────────
              Deterministic (no LLM) — room-level risk from recent work-order
              recurrence (weighted for housekeeping-inspection-caught defects)
              + occupancy load, and asset-level risk from age-vs-expected-life
              + recent fault frequency, independent of the calendar PM
              schedule above. See backend/predictiveMaintenance.js. */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: '0.75rem', marginBottom: '1.2rem' }}>
            <div style={{ ...card }}>
              <h3 style={secHead}>Predicted Room Risk</h3>
              <div style={{ fontSize: '0.76rem', color: C.textMuted, marginTop: '-0.4rem', marginBottom: '0.6rem' }}>
                Rooms trending toward an issue, from recent work-order recurrence + occupancy load
              </div>
              {riskLoading && !riskData ? (
                <div style={{ fontSize: '0.84rem', color: C.textMuted, textAlign: 'center', padding: '1rem 0' }}>Loading...</div>
              ) : (riskData?.room_risk || []).length === 0 ? (
                <div style={{ fontSize: '0.84rem', color: C.textMuted, textAlign: 'center', padding: '1rem 0' }}>No rooms flagged</div>
              ) : (riskData.room_risk.map((r) => (
                <div key={r.room_id} style={{ padding: '0.5rem 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, color: C.text, fontSize: '0.85rem' }}>Room {r.room_number}</span>
                    <span style={{
                      padding: '0.12rem 0.5rem', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700,
                      background: (r.severity === 'HIGH' ? C.danger : C.warn) + '18',
                      color: r.severity === 'HIGH' ? C.danger : C.warn
                    }}>{r.severity}</span>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: C.textMuted, marginTop: '0.15rem' }}>{r.description}</div>
                </div>
              )))}
            </div>

            <div style={{ ...card }}>
              <h3 style={secHead}>Predicted Asset Risk</h3>
              <div style={{ fontSize: '0.76rem', color: C.textMuted, marginTop: '-0.4rem', marginBottom: '0.6rem' }}>
                Assets nearing end-of-life or faulting often, ahead of their next scheduled service
              </div>
              {riskLoading && !riskData ? (
                <div style={{ fontSize: '0.84rem', color: C.textMuted, textAlign: 'center', padding: '1rem 0' }}>Loading...</div>
              ) : (riskData?.asset_risk || []).length === 0 ? (
                <div style={{ fontSize: '0.84rem', color: C.textMuted, textAlign: 'center', padding: '1rem 0' }}>No assets flagged</div>
              ) : (riskData.asset_risk.map((a) => (
                <div key={a.asset_id} style={{ padding: '0.5rem 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, color: C.text, fontSize: '0.85rem' }}>{a.asset_name}</span>
                    <span style={{
                      padding: '0.12rem 0.5rem', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700,
                      background: (PRIORITY_COLOURS[a.severity] || '#6c757d') + '18',
                      color: PRIORITY_COLOURS[a.severity] || '#6c757d'
                    }}>{a.severity}</span>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: C.textMuted, marginTop: '0.15rem' }}>{a.description}</div>
                </div>
              )))}
            </div>
          </div>

          {/* ── Recent Work Orders Table ───────────────────────────────────── */}
          <div style={{ ...card }}>
            <h3 style={secHead}>Recent Work Orders</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['WO #', 'Room', 'Category', 'Priority', 'Status', 'Created'].map((h) => (
                      <th key={h} style={{
                        padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 700,
                        color: C.primary, borderBottom: `2px solid ${C.border}`,
                        fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.03em',
                        whiteSpace: 'nowrap'
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentWOs.length > 0 ? recentWOs.map((wo, idx) => (
                    <tr key={wo.work_order_no || idx} style={{ background: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={{ padding: '0.55rem 0.75rem', borderBottom: '1px solid #f0f0f0', fontWeight: 600, color: C.accent }}>
                        {wo.work_order_no || '-'}
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', borderBottom: '1px solid #f0f0f0', color: C.text }}>
                        {wo.room_number || '-'}
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', borderBottom: '1px solid #f0f0f0', color: C.text }}>
                        {wo.category || '-'}
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{
                          display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: '12px',
                          fontSize: '0.76rem', fontWeight: 700,
                          background: (PRIORITY_COLOURS[wo.priority] || '#6c757d') + '18',
                          color: PRIORITY_COLOURS[wo.priority] || '#6c757d'
                        }}>
                          {wo.priority || '-'}
                        </span>
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', borderBottom: '1px solid #f0f0f0' }}>
                        <span style={{
                          display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: '12px',
                          fontSize: '0.76rem', fontWeight: 600,
                          background: (STATUS_COLOURS[wo.status] || '#6c757d') + '18',
                          color: STATUS_COLOURS[wo.status] || '#6c757d'
                        }}>
                          {(wo.status || '-').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', borderBottom: '1px solid #f0f0f0', color: C.textMuted, whiteSpace: 'nowrap' }}>
                        {fmtDateTime(wo.created_at)}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={6} style={{ padding: '1.5rem', textAlign: 'center', color: C.textMuted }}>
                        No recent work orders
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default MaintDashboard;
